import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { Price } from "../components/ui/Price";
import { useApi } from "../context/ApiContext";
import { useStore } from "../context/StoreContext";
import { getResizedImageUrl, ImagePresets } from "../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../hooks/useStoreLogoPlaceholder";
import { findProductImage } from "../utils/productImageUtils";
import CustomizationImageViewer from "../components/productCustomization/CustomizationImageViewer";
import ThreeDModelViewer from "../components/productCustomization/ThreeDModelViewer";
import { TrackingTimeline } from "../components/order/TrackingTimeline";
import { useTranslation } from "react-i18next";

const openWhatsApp = (phoneNumber: string, orderNumber: string): void => {
  // Remove any non-digit characters except +
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, "");
  // Remove + if present and add it back, ensure it starts with +
  const formattedPhone = cleanPhone.startsWith("+")
    ? cleanPhone
    : `+${cleanPhone}`;
  const message = encodeURIComponent(
    `Hello! I have a question about my order ${orderNumber}.`,
  );
  const whatsappUrl = `https://wa.me/${formattedPhone.replace("+", "")}?text=${message}`;
  window.open(whatsappUrl, "_blank");
};

interface OrderItem {
  _id?: string;
  product?: {
    _id: string;
    handle?: string;
    title: string;
    images?: Array<{ url: string; isPrimary?: boolean }>;
    variantOptions?: Array<any>;
    variantCombinations?: Array<any>;
    customizationType?: "2d-print" | "3d-print";
    canvasSize?: string;
  };
  productTitle?: string;
  variant?: {
    name: string;
  };
  variantName?: string;
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>;
  sku?: string;
  quantity: number;
  price: number;
  customization?:
    | string
    | {
        _id: string;
        previewImage?: string;
        modelPreviewImage?: string;
        customizationType?: "2d-print" | "3d-print";
        elements?: Array<any>;
        threeDData?: any;
        canvasSize?: string;
        backgroundColor?: string;
        backgroundImage?: string;
      }; // Customization ID or populated object
  customizationData?: Record<string, any>;
}

interface Order {
  _id: string;
  orderNumber: string;
  email?: string;
  status:
    | "pending"
    | "confirmed"
    | "processing"
    | "shipped"
    | "delivered"
    | "cancelled"
    | "refunded";
  items: OrderItem[];
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  currency?: string;
  locationId?: {
    _id?: string;
    whatsappPhoneNumber?: string;
  };
  shippingAddress: {
    firstName: string;
    lastName: string;
    address1: string;
    address2?: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    phone?: string;
  };
  billingAddress: {
    firstName: string;
    lastName: string;
    address1: string;
    address2?: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    phone?: string;
  };
  shippingMethod?: {
    name: string;
    estimatedDays?: { min: number; max: number };
  };
  trackingNumber?: string;
  shippingCarrierProvider?: string;
  shippingCarrierTrackingNumber?: string;
  trackingEvents?: Array<{
    status: string;
    location?: string;
    timestamp: string;
    description?: string;
  }>;
  paymentMethod?: "cash_on_delivery" | "pay_by_invoice" | "online_payment";
  payment?: {
    _id: string;
    status: "pending" | "processing" | "completed" | "failed" | "refunded";
    method: "stripe" | "paypal" | "other";
    transactionId?: string;
    amount: number;
    refundedAmount?: number;
    failureReason?: string;
  };
  cancelledAt?: string;
  cancelledReason?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  customOrderDate?: string;
  isAdminCreated?: boolean;
}

export const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { api } = useApi();
  const { isPluginEnabled } = useStore();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const isCustomizationPluginEnabled = isPluginEnabled("product-customization");
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingCustomization, setViewingCustomization] = useState<{
    id: string;
    type: "2d-print" | "3d-print";
  } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const { t } = useTranslation();
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch order");
    } finally {
      setLoading(false);
    }
  }, [api, orderId]);

  useEffect(() => {
    if (!orderId) {
      navigate("/orders");
      return;
    }
    fetchOrder();
  }, [orderId, navigate, fetchOrder]);

  // Note: Tracking events are populated when admin syncs tracking
  // Customers will see tracking timeline once events are available

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "refunded":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get current shipping status from tracking events
  const getShippingStatus = () => {
    if (!order?.trackingEvents || order.trackingEvents.length === 0) {
      // Fallback to order status if no tracking events
      if (order?.status === "shipped" || order?.status === "delivered") {
        return {
          status: order.status,
          description: order.status === "delivered" 
            ? "Your order has been delivered" 
            : "Your order is on the way",
        };
      }
      return null;
    }

    // Get the most recent tracking event (they should be sorted by timestamp)
    const latestEvent = order.trackingEvents[0];
    const statusLower = latestEvent.status?.toLowerCase() || "";

    // Map tracking status to user-friendly messages
    if (statusLower.includes("delivered")) {
      return {
        status: "delivered",
        description: latestEvent.description || "Your order has been delivered",
        location: latestEvent.location,
      };
    }
    if (statusLower.includes("out_for_delivery") || statusLower.includes("out for delivery")) {
      return {
        status: "out_for_delivery",
        description: latestEvent.description || "Your order is out for delivery",
        location: latestEvent.location,
      };
    }
    if (statusLower.includes("in_transit") || statusLower.includes("transit") || statusLower.includes("shipped")) {
      return {
        status: "in_transit",
        description: latestEvent.description || "Your order is in transit",
        location: latestEvent.location,
      };
    }
    if (statusLower.includes("processing") || statusLower.includes("preparing")) {
      return {
        status: "processing",
        description: latestEvent.description || "Your order is being prepared",
        location: latestEvent.location,
      };
    }

    return {
      status: latestEvent.status || "unknown",
      description: latestEvent.description || latestEvent.status,
      location: latestEvent.location,
    };
  };

  const getShippingStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("delivered")) {
      return "bg-green-100 text-green-800 border-green-200";
    }
    if (statusLower.includes("out_for_delivery") || statusLower.includes("out for delivery")) {
      return "bg-blue-100 text-blue-800 border-blue-200";
    }
    if (statusLower.includes("in_transit") || statusLower.includes("transit") || statusLower.includes("shipped")) {
      return "bg-blue-50 text-blue-700 border-blue-200";
    }
    if (statusLower.includes("processing") || statusLower.includes("preparing")) {
      return "bg-yellow-50 text-yellow-700 border-yellow-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getCarrierName = (provider?: string) => {
    if (!provider) return null;
    const providerUpper = provider.toUpperCase();
    switch (providerUpper) {
      case "FLEETRUNNR":
        return "Fleetrunnr";
      case "DHL":
        return "DHL";
      case "GLS":
        return "GLS";
      case "DPD":
        return "DPD";
      default:
        return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
  };

  const shippingStatus = getShippingStatus();

  const canCancelOrder = () => {
    return (
      order && (order.status === "pending" || order.status === "confirmed")
    );
  };

  const handleCancelOrder = async () => {
    if (!orderId || !cancelReason.trim()) {
      setError("Please provide a reason for cancellation");
      return;
    }

    try {
      setCancelling(true);
      setError(null);
      await api.post(`/orders/${orderId}/cancel`, {
        reason: cancelReason.trim(),
      });
      // Refresh order data
      await fetchOrder();
      setShowCancelDialog(false);
      setCancelReason("");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const handleViewInvoice = async () => {
    if (!orderId) return;

    try {
      setLoadingInvoice(true);
      setError(null);

      // Download invoice PDF from bucket
      const response = await api.get(`/invoices/order/${orderId}/pdf`, {
        responseType: "blob", // Get as blob for PDF download
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `invoice-${order?.orderNumber || orderId}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to download invoice. Please make sure you are logged in.",
      );
    } finally {
      setLoadingInvoice(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <Stack spacing="lg">
          {error && <Alert variant="error">{error}</Alert>}
          <Button onClick={() => navigate("/orders")}>Back to Orders</Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="md">
        <div className="flex items-center justify-between">
          <div>
            <Heading size="xl" className="mb-1">
              Order #{order.orderNumber}
            </Heading>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}
              >
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              <span>•</span>
              <span>
                {t("orderDetail.orderPlacedOn")}:{" "}
                {new Date(
                  order.customOrderDate || order.createdAt,
                ).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {["confirmed", "shipped", "delivered"].includes(
              order?.status || "",
            ) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewInvoice}
                disabled={loadingInvoice}
              >
                {loadingInvoice
                  ? t("common.loading")
                  : t("orderDetail.viewInvoice")}
              </Button>
            )}
            {order.locationId &&
              typeof order.locationId === "object" &&
              order.locationId.whatsappPhoneNumber && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (
                      order.locationId &&
                      typeof order.locationId === "object" &&
                      order.locationId.whatsappPhoneNumber
                    ) {
                      openWhatsApp(
                        order.locationId.whatsappPhoneNumber,
                        order.orderNumber,
                      );
                    }
                  }}
                  className="bg-green-50 text-green-700 border-green-500 hover:bg-green-100"
                >
                  📱 WhatsApp
                </Button>
              )}
            {canCancelOrder() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                className="text-red-600 border-red-600 hover:bg-red-50"
              >
                {t("orderDetail.cancelOrder")}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* Shipping Status & Tracking Info */}
            {(order.trackingNumber ||
              order.shippingCarrierTrackingNumber ||
              order.trackingEvents?.length ||
              order.shippingMethod ||
              shippingStatus) && (
              <Card padding="md">
                <div className="space-y-4">
                  {/* Shipping Status Badge */}
                  {shippingStatus && (
                    <div className={`rounded-lg border-2 p-4 ${getShippingStatusColor(shippingStatus.status)}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="text-sm font-semibold mb-1">
                            Shipping Status
                          </div>
                          <div className="text-base font-medium mb-1">
                            {shippingStatus.description}
                          </div>
                          {shippingStatus.location && (
                            <div className="text-xs opacity-75 mt-1">
                              📍 {shippingStatus.location}
                            </div>
                          )}
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getShippingStatusColor(shippingStatus.status)}`}>
                          {shippingStatus.status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Carrier & Tracking Info */}
                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    {order.shippingCarrierProvider && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Carrier</span>
                        <span className="text-gray-900 font-medium">
                          {getCarrierName(order.shippingCarrierProvider)}
                        </span>
                      </div>
                    )}
                    {(order.trackingNumber ||
                      order.shippingCarrierTrackingNumber) && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Tracking Number</span>
                        <span className="text-sm font-mono text-gray-900 font-medium">
                          {order.shippingCarrierTrackingNumber ||
                            order.trackingNumber}
                        </span>
                      </div>
                    )}
                    {order.shippingMethod && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Shipping Method</span>
                        <span className="text-gray-900">
                          {order.shippingMethod.name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Tracking Timeline */}
                  {order.trackingEvents && order.trackingEvents.length > 0 ? (
                    <div className="pt-3 border-t border-gray-100">
                      <TrackingTimeline
                        events={order.trackingEvents}
                        currentStatus={order.status}
                      />
                    </div>
                  ) : order.shippingCarrierTrackingNumber ? (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="text-sm text-gray-500 text-center py-4">
                        Tracking information will be available once your order is processed by the carrier.
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>
            )}

            {order.cancelledAt && (
              <Card padding="md" className="bg-red-50 border-red-200">
                <div className="text-sm">
                  <p className="text-red-800 font-medium">
                    {t("orderDetail.cancelled")}:{" "}
                    {new Date(order.cancelledAt).toLocaleDateString()}
                  </p>
                  {order.cancelledReason && (
                    <p className="text-red-700 mt-1 text-xs">
                      {t("orderDetail.reason")}: {order.cancelledReason}
                    </p>
                  )}
                </div>
              </Card>
            )}

            {/* Order Items */}
            <Card padding="md">
              <Stack spacing="sm">
                <Heading size="md">Order Items</Heading>
                <div className="space-y-3">
                  {order.items.map((item, idx) => {
                    const productTitle =
                      item.productTitle || item.product?.title || "Product";
                    const variantName = item.variantName || item.variant?.name;
                    // Only try to find variant-specific image if product is populated and has images
                    const image =
                      item.product &&
                      item.product.images &&
                      item.product.images.length > 0
                        ? findProductImage(
                            item.product,
                            item.selectedVariantOptions,
                          )
                        : undefined;
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.checkoutReview,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const productUrl = item.product
                      ? `/products/${item.product.handle || item.product._id}`
                      : null;
                    return (
                      <div
                        key={item._id || idx}
                        className="flex gap-3 pb-3 border-b border-gray-100 last:border-0"
                      >
                        {productUrl ? (
                          <Link
                            to={productUrl}
                            className="w-16 h-16 object-cover rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={imageUrl}
                              alt={productTitle}
                              className="w-16 h-16 object-cover rounded flex-shrink-0"
                            />
                          </Link>
                        ) : (
                          <img
                            src={imageUrl}
                            alt={productTitle}
                            className="w-16 h-16 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <div className="flex-1">
                          {productUrl ? (
                            <Link
                              to={productUrl}
                              className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                            >
                              {productTitle}
                            </Link>
                          ) : (
                            <div className="font-medium text-gray-900">
                              {productTitle}
                            </div>
                          )}
                          {variantName && (
                            <div className="text-sm text-gray-500">
                              {t("orderDetail.variant")}: {variantName}
                            </div>
                          )}
                          {item.selectedVariantOptions &&
                            item.selectedVariantOptions.length > 0 && (
                              <div className="space-y-0.5">
                                {item.selectedVariantOptions.map(
                                  (option, idx) => (
                                    <div
                                      key={idx}
                                      className="text-sm text-gray-500"
                                    >
                                      {option.optionName}: {option.optionValue}
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                          {item.sku && (
                            <div className="text-sm text-gray-500">
                              {t("orderDetail.sku")}: {item.sku}
                            </div>
                          )}
                          <div className="text-sm text-gray-500">
                            {t("orderDetail.quantity")}: {item.quantity}
                          </div>
                          {item.customization &&
                            isCustomizationPluginEnabled &&
                            (() => {
                              const customizationId =
                                typeof item.customization === "string"
                                  ? item.customization
                                  : item.customization._id;
                              const customizationObj =
                                typeof item.customization === "object"
                                  ? item.customization
                                  : null;
                              const customizationType =
                                customizationObj?.customizationType ||
                                item.product?.customizationType;

                              return (
                                <div className="text-sm text-gray-500 mt-2">
                                  <span className="font-medium">
                                    Customization:
                                  </span>
                                  <div className="mt-2">
                                    {(customizationType === "2d-print" ||
                                      customizationType === "3d-print") && (
                                      <img
                                        src={
                                          customizationObj?.previewImage ||
                                          customizationObj?.modelPreviewImage
                                        }
                                        alt="Customization preview"
                                        className="w-24 h-24 object-contain border border-gray-200 rounded cursor-pointer hover:opacity-80 transition-opacity mt-1"
                                        onClick={() =>
                                          setViewingCustomization({
                                            id: customizationId,
                                            type: customizationType,
                                          })
                                        }
                                        title={
                                          t("orderDetail.viewFullSize") ||
                                          "Click to view full size"
                                        }
                                      />
                                    )}
                                    {!customizationObj && (
                                      <button
                                        onClick={() =>
                                          setViewingCustomization({
                                            id: customizationId,
                                            type: (item.product
                                              ?.customizationType ||
                                              "2d-print") as
                                              | "2d-print"
                                              | "3d-print",
                                          })
                                        }
                                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1"
                                      >
                                        {t("orderDetail.viewCustomization")}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          {item.customizationData &&
                            Object.keys(item.customizationData).length > 0 && (
                              <div className="text-sm text-gray-500 mt-1">
                                <span className="font-medium">
                                  Customization Data:
                                </span>
                                <ul className="list-disc list-inside ml-2">
                                  {Object.entries(item.customizationData).map(
                                    ([key, value]) => (
                                      <li key={key}>
                                        {key}: {String(value)}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>
                        <div className="text-right">
                          <Price
                            amount={item.price * item.quantity}
                            currency={order.currency || "USD"}
                            size="md"
                            className="font-medium text-gray-900"
                          />
                          <div className="text-sm text-gray-500 mt-1">
                            <Price
                              amount={item.price}
                              currency={order.currency || "USD"}
                              size="sm"
                              showCurrency={false}
                            />{" "}
                            each
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Stack>
            </Card>

            {/* Shipping Address */}
            {order.shippingAddress && (
              <Card padding="md">
                <Stack spacing="sm">
                  <Heading size="md">Shipping Address</Heading>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>
                      {order.shippingAddress.firstName}{" "}
                      {order.shippingAddress.lastName}
                    </div>
                    <div>{order.shippingAddress.address1}</div>
                    {order.shippingAddress.address2 && (
                      <div>{order.shippingAddress.address2}</div>
                    )}
                    <div>
                      {order.shippingAddress.city},{" "}
                      {order.shippingAddress.province}{" "}
                      {order.shippingAddress.postalCode}
                    </div>
                    <div>{order.shippingAddress.country}</div>
                    {order.shippingAddress.phone && (
                      <div>Phone: {order.shippingAddress.phone}</div>
                    )}
                  </div>
                </Stack>
              </Card>
            )}

            {/* Billing Address */}
            {order.billingAddress &&
              order.shippingAddress &&
              (order.billingAddress.address1 !==
                order.shippingAddress.address1 ||
                order.billingAddress.city !== order.shippingAddress.city ||
                order.billingAddress.postalCode !==
                  order.shippingAddress.postalCode ||
                order.billingAddress.country !==
                  order.shippingAddress.country) && (
                <Card padding="md">
                  <Stack spacing="sm">
                    <Heading size="md">Billing Address</Heading>
                    <div className="text-sm text-gray-700 space-y-1">
                      <div>
                        {order.billingAddress.firstName}{" "}
                        {order.billingAddress.lastName}
                      </div>
                      <div>{order.billingAddress.address1}</div>
                      {order.billingAddress.address2 && (
                        <div>{order.billingAddress.address2}</div>
                      )}
                      <div>
                        {order.billingAddress.city},{" "}
                        {order.billingAddress.province}{" "}
                        {order.billingAddress.postalCode}
                      </div>
                      <div>{order.billingAddress.country}</div>
                      {order.billingAddress.phone && (
                        <div>Phone: {order.billingAddress.phone}</div>
                      )}
                    </div>
                  </Stack>
                </Card>
              )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="space-y-4">
            <Card padding="md">
              <Stack spacing="sm">
                <Heading size="md">Summary</Heading>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <Price
                      amount={order.subtotal}
                      currency={order.currency || "USD"}
                      size="sm"
                      className="text-gray-900"
                    />
                  </div>
                  {order.tax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax</span>
                      <Price
                        amount={order.tax}
                        currency={order.currency || "USD"}
                        size="sm"
                        className="text-gray-900"
                      />
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <Price
                      amount={order.shipping || 0}
                      currency={order.currency || "USD"}
                      size="sm"
                      className="text-gray-900"
                    />
                  </div>
                  <hr />
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">Total</span>
                    <Price
                      amount={
                        order.total ||
                        order.subtotal +
                          (order.tax || 0) +
                          (order.shipping || 0)
                      }
                      currency={order.currency || "USD"}
                      size="lg"
                      className="font-semibold text-gray-900"
                    />
                  </div>
                </div>

                {/* Payment Information - Consolidated */}
                {(order.paymentMethod || order.payment) && (
                  <>
                    <hr className="my-3" />
                    <div className="space-y-2">
                      {order.paymentMethod && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Payment Method</span>
                          <span className="text-gray-900 capitalize">
                            {order.paymentMethod.replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                      {order.payment && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              Payment Status
                            </span>
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                order.payment.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : order.payment.status === "processing"
                                    ? "bg-blue-100 text-blue-800"
                                    : order.payment.status === "pending"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : order.payment.status === "failed"
                                        ? "bg-red-100 text-red-800"
                                        : order.payment.status === "refunded"
                                          ? "bg-purple-100 text-purple-800"
                                          : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {order.payment.status.charAt(0).toUpperCase() +
                                order.payment.status.slice(1)}
                            </span>
                          </div>
                          {order.payment.refundedAmount && (
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>Refunded</span>
                              <Price
                                amount={order.payment.refundedAmount}
                                currency={order.currency || "USD"}
                                size="xs"
                                showCurrency={true}
                              />
                            </div>
                          )}
                          {order.payment.failureReason && (
                            <div className="text-xs text-red-600 pt-1 border-t border-gray-100">
                              Reason: {order.payment.failureReason}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </Stack>
            </Card>
          </div>
        </div>

        {/* Cancel Order Dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card padding="lg" className="max-w-md w-full mx-4">
              <Stack spacing="md">
                <Heading size="lg">Cancel Order</Heading>
                <p className="text-gray-600">
                  {t("orderDetail.cancelOrderConfirmation")} #
                  {order.orderNumber}?{" "}
                  {t("orderDetail.cancelOrderConfirmationDescription")}
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("orderDetail.reasonForCancellation")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder={t(
                      "common.placeholders.provideCancellationReason",
                    )}
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>
                {error && <Alert variant="error">{error}</Alert>}
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCancelDialog(false);
                      setCancelReason("");
                      setError(null);
                    }}
                    disabled={cancelling}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCancelOrder}
                    disabled={cancelling || !cancelReason.trim()}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {cancelling
                      ? t("common.cancelling")
                      : t("orderDetail.confirmCancellation")}
                  </Button>
                </div>
              </Stack>
            </Card>
          </div>
        )}

        {/* Customization Viewers */}
        {isCustomizationPluginEnabled &&
          viewingCustomization &&
          viewingCustomization.type === "2d-print" && (
            <CustomizationImageViewer
              customizationId={viewingCustomization.id}
              onClose={() => setViewingCustomization(null)}
            />
          )}
        {isCustomizationPluginEnabled &&
          viewingCustomization &&
          viewingCustomization.type === "3d-print" &&
          (() => {
            const customizationObj = order.items.find(
              (item: any) =>
                (typeof item.customization === "string"
                  ? item.customization
                  : item.customization?._id) === viewingCustomization.id,
            )?.customization;
            const previewUrl =
              typeof customizationObj === "object"
                ? customizationObj?.previewImage ||
                  customizationObj?.modelPreviewImage
                : undefined;

            return (
              <ThreeDModelViewer
                customizationId={viewingCustomization.id}
                previewUrl={previewUrl}
                onClose={() => setViewingCustomization(null)}
              />
            );
          })()}
      </Stack>
    </Container>
  );
};

export default OrderDetail;
